<script context="module">
  import { encodingToHex, getPalette, hexToEncoding } from "bitframes";
  import { renderToCanvas } from "bitframes";
  import { colorSpace, getBackground } from "../util/constants";
  import { CachedRenderer, CanvasPoolManager } from "../util/fast-canvas-pool";

  const canvasPoolCache = CachedRenderer({ size: 128 });

  const IS_ZOOM = false;
  const DEFAULT_BACKGROUND = getBackground({ system: 0x00 });
  const GALLERY_BACKGROUND = IS_ZOOM ? DEFAULT_BACKGROUND : "initial";
  const BIG_NUMBER = 1000000;
  const toHex = (e) =>
    e ? (typeof e === "string" ? e : encodingToHex(e)) : null;

  let globalId = 0;
  let raf;
  const renderQueue = [];

  function* limitedIterator(set, max = Infinity, reverse = false) {
    let index = 0;
    const elementsToDelete = new Set();
    const curSet = [...set];
    if (reverse) curSet.reverse();
    for (let e of curSet) {
      if (index >= max) {
        break;
      }
      yield e;
      elementsToDelete.add(e);
      index++;
    }
    for (let e of elementsToDelete) {
      set.delete(e);
    }
  }

  async function doRender() {
    const maxPerFrame = 2;

    for (let el of limitedIterator(renderQueue, maxPerFrame, true)) {
      // console.log("gotta render", el);
    }

    if (renderQueue.size > 0) {
      requestRenderFrame();
    }
  }

  function requestRenderFrame() {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(doRender);
  }

  function stopAnimation() {
    cancelAnimationFrame(raf);
  }

  function addToRenderQueue(node) {
    if (!renderQueue.has(node)) {
      renderQueue.add(node);
      requestRenderFrame();
    }
  }

  function removeFromRenderQueue(node) {
    if (renderQueue.has(node)) {
      renderQueue.delete(node);
    }
  }

  function createThumb() {
    return {
      canvas: null,
      context: null,
      encoding: null,
      active: false,
      create(w = 128, h = 128) {
        this.ready = null;
        this.canvas = document.createElement("canvas");
        this.context = this.canvas.getContext("2d", { colorSpace });
        this.canvas.width = w;
        this.canvas.height = h;
        this.canvas.style.width = this.canvas.style.height = "100%";
        this.canvas.style.imageRendering = "pixelated";
      },
      ensure(w, h) {
        if (!this.canvas) this.create(w, h);
      },
      diff(encoding, w, h) {
        // const newEncodingHex =
        //   typeof encoding === "string" ? encoding : encodingToHex(encoding);
        const encodingChanged = this.encoding !== encoding;
        const sizeChanged = this.canvas.width !== w || this.canvas.height !== h;
        return encodingChanged || sizeChanged;
      },
      enqueue() {},
      hide() {
        if (this.canvas) {
          this.canvas.style.visibility = "hidden";
          this.ready = null;
        }
      },
      render(encoding, w, h) {
        this.ensure(w, h);

        const newEncodingHex =
          typeof encoding === "string" ? encoding : encodingToHex(encoding);
        const encodingChanged = this.encoding !== encoding;
        const sizeChanged = this.canvas.width !== w || this.canvas.height !== h;

        let didChange = false;
        if (encodingChanged || sizeChanged) {
          didChange = true;
          const encodingBytes =
            typeof encoding === "string" ? hexToEncoding(encoding) : encoding;
          if (sizeChanged) {
            this.canvas.width = w;
            this.canvas.height = h;
          }
          this.encoding = newEncodingHex;
          this.context.clearRect(0, 0, w, h);

          const curID = globalId;
          globalId = (globalId + 1) % BIG_NUMBER;
          // this.hide();
          // console.log(curID);
          this.id = curID;
          // this.ready = drawCanvas2D({
          //   id: curID,
          //   context: this.context,
          //   width: w,
          //   height: h,
          //   colorSpace,
          //   encoding: newEncodingHex,
          //   hatch: false,
          //   hatchContours: false,
          // }).then((id) => {
          //   if (id === this.id && this.active && this.ready) {
          //     this.canvas.style.visibility = "";
          //   }
          // });
          renderToCanvas({
            colorSpace,
            width: w,
            height: h,
            encoding: encodingBytes,
            context: this.context,
            margin: IS_ZOOM ? 0 : undefined,
            hatch: false,
            hatchContours: false,
          });
        }
        // if (this.ready) await this.ready;
        this.canvas.style.visibility = "";
      },
    };
  }

  const INITIAL_CANVASES = 0;
  const canvasPool = Array(INITIAL_CANVASES)
    .fill()
    .map(() => createThumb());

  function release(index) {
    if (canvasPool[index]) {
      const c = canvasPool[index];
      c.active = false;
      if (c.canvas && c.canvas.parentElement) {
        c.canvas.parentElement.removeChild(c.canvas);
      }
      if (c.raf != null) {
        cancelAnimationFrame(c.raf);
      }
      c.raf = null;
    }
  }

  function renderTo(node, encoding, w, h) {
    const container = node.firstElementChild;

    // first, hide previous
    const curChild = container.firstElementChild;

    // get first non active thumb
    let thumbIdx = canvasPool.findIndex((c) => !c.active);

    // all are active, or there's none in array, so push
    if (thumbIdx == -1) {
      canvasPool.push(createThumb());
      thumbIdx = canvasPool.length - 1;
    }

    // get this thumb
    let thumb = canvasPool[thumbIdx];

    // mark as active
    thumb.active = true;

    const encodingHex = toHex(encoding);

    if (thumb.raf != null) cancelAnimationFrame(thumb.raf);
    thumb.raf = null;

    // check if we need to render
    if (!thumb.canvas || thumb.diff(encodingHex, w, h) || !curChild) {
      // if canvas does exist, hide it
      if (thumb.canvas) {
        // thumb.canvas.style.visibility = "hidden";
        thumb.hide();
      }

      thumb.raf = requestAnimationFrame(() => {
        thumb.ensure(w, h);

        // now add the thumb to the element
        const curChild = container.firstElementChild;
        if (curChild === thumb.canvas) {
          // do nothing, already mounted
        } else {
          if (curChild) {
            container.removeChild(curChild);
          }
          container.appendChild(thumb.canvas);
        }

        thumb.render(encoding, w, h);
      });
    }

    return thumbIdx;
  }
</script>

<script>
  import { createEventDispatcher, onDestroy, onMount } from "svelte";
  // import favHeart from "../assets/icons/heart4.svg";
  // import { drawCanvas2D, getWorker } from "../util/worker.pool";

  export let encodings = [];

  export let encoding = null;
  // export let filterFavorites = false;

  let curEncodingHex = toHex(encoding);
  $: curEncodingHex = toHex(encoding);
  $: encodings, checkEncodings();

  export let usingFavs = false;

  // let showFav = true;
  let curHighlight = null;

  export let size = 64;
  export let gap = 10;
  export let renderSize = 128;

  // const resizeObserver = new ResizeObserver((entries) => {
  //   const e = entries[0];
  //   const { width, height } = e.contentRect;
  //   // console.log(
  //   //   Math.floor(width / (size + gap)),
  //   //   Math.round(width / (size + gap)),
  //   //   Math.ceil(width / (size + gap))
  //   // );
  //   const curColumns = Math.ceil(width / (size + gap));
  // });

  const pool = CanvasPoolManager({
    hatch: false,
    hatchContours: false,
    pixelated: true,
    maxPaintsPerFrame: 16,
    maxPerFrame: 128,
    cache: canvasPoolCache,
    colorSpace,
    width: renderSize,
    height: renderSize,
  });

  const poolAction = pool.action;

  onDestroy(() => {
    pool.destroy();
  });

  const dispatch = createEventDispatcher();
  let container;
  // let intersection = new IntersectionObserver(
  //   (entries) => {
  //     for (let event of entries) {
  //       const el = event.target;
  //       if (event.isIntersecting) {
  //         triggerElement(el);
  //       } else {
  //         if (el.dataset.visible == "true") {
  //           el.dataset.visible = "false";
  //           let pidx = el.dataset.poolIndex;
  //           if (pidx != null) {
  //             pidx = parseInt(pidx, 10);
  //             if (isFinite(pidx) && pidx >= 0) {
  //               release(pidx);
  //               el.dataset.poolIndex = "";
  //             }
  //           }
  //         }
  //       }
  //     }
  //   },
  //   {
  //     root: container,
  //   }
  // );

  // function triggerElement(el) {
  //   el.dataset.visible = "true";

  //   const encoding = el.dataset.encoding;
  //   const index = renderTo(el, encoding, renderSize, renderSize);
  //   el.dataset.poolIndex = String(index);
  // }

  // function observer(node) {
  //   if (intersection) {
  //     intersection.observe(node);
  //   }
  //   return {
  //     destroy: () => {
  //       if (intersection) intersection.unobserve(node);
  //     },
  //   };
  // }

  function checkEncodings() {
    if (!container) return;
    const expectedEncodings = (encodings || []).slice();
    const children = container.children;
    for (let i = 0; i < children.length; i++) {
      const c = children[i];
      if (i < expectedEncodings.length) {
        const item = expectedEncodings[i];
        const hex = toHex(item[0]);
        const cur = c.dataset.encoding;
        if (cur !== hex) {
          c.dataset.encoding = hex;
          if (c.dataset.visible == "true") {
            // triggerElement(c);
            console.log("trigger");
            pool.addToRenderQueue(c);
          }
        }
      } else {
        console.log("child is dirty");
      }
    }
  }

  function computeColumns(width) {
    return Math.floor(width / (size - gap));
  }

  function columnSizer(node) {
    const { width } = node.getBoundingClientRect();
    // const cols = computeColumns(width);
    // // node.style.setProperty("--columns", cols);
    resizeObserver.observe(node);
    return {
      destroy() {
        resizeObserver.unobserve(node);
      },
    };
  }

  // export function updateVisible() {
  //   if (!container) return;
  //   var children = container.children;
  //   for (c of children) {
  //     if (c.dataset.visible == "true") {
  //       // triggerElement(c);
  //     }
  //   }
  // }

  function favoriteOnDoubleTap(node, opts = {}) {
    const { itemEncoding } = opts;

    let last = 0;
    const handler = (ev) => {
      const now = Date.now();
      const delta = now - last;
      if (delta > 0 && delta < 500) {
        ev.preventDefault();
        ev.stopImmediatePropagation();
        last = 0;
        dispatch("favorite", itemEncoding);
      } else {
        last = now;
      }
    };
    node.addEventListener("touchend", handler, { passive: false });
    // dispatch("favorite", itemEncoding);
    return {
      update() {},
      destroy() {
        node.removeEventListener("touchend", handler);
      },
    };
  }

  export function findElement(encodingHex) {
    if (!container) return null;
    const match = container.querySelector(
      `.cell[data-encoding="${encodingHex}"]`
    );
    return match;
  }

  function isSystem01(enc) {
    return toHex(enc).charAt(1) == "1";
  }
</script>

<div
  class="container noselect"
  bind:this={container}
  style={`--gap: ${gap}px; --size: ${size}px; background-color: ${GALLERY_BACKGROUND};`}
>
  {#each encodings as [itemEncoding, fav], i}
    <div
      class:selected={toHex(itemEncoding) === curEncodingHex}
      class:system1={isSystem01(itemEncoding)}
      style={`background-color: ${getBackground({ system: isSystem01(itemEncoding) ? 0x01 : 0x00 })}`}
      class="cell"
      use:poolAction
      on:mouseenter={(ev) => {
        if (usingFavs) {
          curHighlight = ev.currentTarget.dataset.encoding;
        }
      }}
      on:mouseleave={(ev) => {
        if (usingFavs) {
          if (curHighlight) {
            if (ev.currentTarget.dataset.encoding === curHighlight) {
              curHighlight = null;
            }
          }
        }
      }}
      data-visible="false"
      data-index={i}
      data-encoding={toHex(itemEncoding)}
      role="none"
      use:favoriteOnDoubleTap={{ itemEncoding }}
      on:dblclick|nonpassive={(ev) => {
        if (usingFavs) {
          ev.preventDefault();
          ev.stopImmediatePropagation();
          dispatch("favorite", itemEncoding);
        }
      }}
      on:touchstart|nonpassive={(ev) => {
        // ev.preventDefault();
        // ev.stopImmediatePropagation();
        // dispatch("select", itemEncoding);
      }}
      on:click|nonpassive={(ev) => {
        ev.preventDefault();
        dispatch("select", itemEncoding);
      }}
    >
      <div class="cell-container"></div>
      {#if usingFavs && (fav || curHighlight === toHex(itemEncoding))}
        <div
          role="none"
          class:favorite={fav}
          on:click={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            dispatch("favorite", itemEncoding);
          }}
          class="fav-button"
        >
          <!-- <svg
            width="21"
            height="21"
            viewBox="0 0 21 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {#if fav}
              <path
                d="M18.2792 4.40562C15.2542 1.97492 12.929 4.41693 11.443 5.84624C9.95705 4.41693 6.84466 2.77225 3.82751 5.19536C3.75483 5.25109 1.19768 6.062 1.30376 8.62303C1.4393 11.9096 5.28296 16.0488 11.0885 19.8359C11.2767 19.9577 11.609 19.9589 11.7976 19.8359C17.6031 16.0488 20.6172 12.4628 20.7537 9.17817C20.8588 6.61518 18.344 4.45593 18.2792 4.40562Z"
                fill="black"
              />{/if}
            <path
              d="M18.1793 4.31578C15.1543 1.88507 12.0419 3.52981 10.5559 4.95911C9.06992 3.52981 5.95753 1.88512 2.94038 4.30823C2.8677 4.36397 1.1401 5.72808 1.24618 8.28911C1.38171 11.5757 4.39583 15.1616 10.2013 18.9488C10.3896 19.0706 10.7219 19.0718 10.9104 18.9488C16.7159 15.1616 19.7301 11.5757 19.8666 8.29104C19.9717 5.72806 18.2441 4.36608 18.1793 4.31578Z"
              fill="currentColor"
            />
          </svg> -->
          <svg
            width="21"
            height="21"
            viewBox="0 0 21 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M4 1H10.5H17V19.5H16.5858L10.5 13.4142L4.41421 19.5H4V1ZM6 2V15.0858L10.5 10.5858L15 15.0858V2H6Z"
              fill="black"
            />
            <path d="M16 2H5V16.5L10.5 11L16 16.5V2Z" fill="currentColor" />
          </svg>

          <!-- <img src={favHeart} alt="" /> -->
        </div>
      {/if}
    </div>
  {/each}
</div>

<style>
  .container {
    --columns: auto-fill;
    --size: 64px;

    /* flex-wrap: nowrap; */
    width: 100%;
    /* height: 100%; */

    /* justify-content: stretch; */
    /* height: 100%; */
    /* height: 100%; */
    /* overflow: -moz-scrollbars-vertical; */
    overflow-y: auto;
    /* height: 100%; */
    flex-grow: 1;
    overflow-x: hidden;
    /* grid-template-columns: repeat(8, 1fr); */
    /* background: pink; */

    /* grid-template-columns: repeat(auto-fill, minmax(32px, 1fr)); */
    /* grid-template-columns: repeat(var(--columns), minmax(var(--size), 1fr)); */
    grid-template-columns: repeat(
      var(--columns),
      minmax(min(100%/4, max(var(--size), 100%/8)), 1fr)
    );

    display: grid;
    /* grid-template-columns: repeat(auto-fit, minmax(64px, 1fr)); */
    /* height: 100%; */
    /* grid-template-columns: repeat(3, minmax(0, 1fr)); */

    /* grid-template: repeat(3, 30vw) / repeat(3, 30vw);
    grid-gap: 10px; */
    /* grid-template-columns: 1fr 1fr 1fr 1fr; */
    /* grid-template-columns: 1fr 1fr 1fr; */
    /* grid-template-rows: 1fr 1fr 1fr; */
    grid-gap: var(--gap);
    padding: 20px;
    box-sizing: border-box;
    place-items: start center;
  }

  .cell-container {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
  }

  .cell {
    -webkit-tap-highlight-color: transparent;
    /* background: red; */
    aspect-ratio: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    /* width: 64px;
    height: 64px; */
    width: 100%;
    height: 100%;
    cursor: pointer;
    touch-action: manipulation;
    position: relative;
  }
  .selected {
    box-shadow:
      3px 3px 0px black,
      3px 0px 0px black,
      0px 3px 0px black;
  }
  .selected.system1 {
    box-shadow:
      3px 3px 0px var(--accent-color),
      3px 0px 0px var(--accent-color),
      0px 3px 0px var(--accent-color);
  }
  .fav-button {
    --unfav-color: hsl(0, 0%, 70%);
    --unfav-opacity: 1;
    position: absolute;
    top: -1px;
    right: 0px;
    width: 22px;
    height: 22px;
    /* z-index: 1; */
    /* background: #e17676; */
    /* border: 1px solid black; */
    border-radius: 100% 100%;
    box-sizing: border-box;
    cursor: pointer;
    opacity: var(--unfav-opacity);
    color: var(--unfav-color);
    padding: 0px;
    /* border: 20px solid transparent; */
  }
  .fav-button svg {
    color: var(--unfav-color);
    /* color: white; */
    /* color: hsl(0, 0%, 85%); */
    /* color: lightcoral; */
  }
  /* .fav-button:hover {

  } */

  .fav-button.favorite svg {
    color: #fff;
    opacity: 1;
    /* color: #ec0d0d; */
  }
  .fav-button:hover svg {
    color: var(--unfav-color);
    /* color: hsl(0, 0%, 85%); */
    /* color: lightcoral; */
  }
  .fav-button.favorite {
    /* background: red; */
    opacity: 1;
  }
  /* .cell-sizer {
    aspect-ratio: 1;
    width: 100%;
    height: 100%;
  } */

  @media screen and (max-width: 720px) {
    /* .fav-button {
      top: -10px;
      right: -10px;
      width: 25px;
      height: 25px;
      padding: 10px;
    } */
  }
</style>
